import React from 'react';
import { Row, Col, Card, Typography } from 'antd';
import {
  SettingOutlined,
  ShopOutlined,
  AppstoreOutlined,
  MenuOutlined,
  TeamOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  PrinterOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import { useIsModuleEnabled } from '../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../constants/moduleKeys';

const { Title, Text } = Typography;

interface ConfigOption {
  key: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  route: string;
  color: string;
}

const ConfiguracionPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  // Get facturacion setting (to show/hide facturacion option)
  const { data: facturacionSetting } = useGetBoolSettingQuery('FacturacionActiva');
  const isFacturacionEnabled = facturacionSetting?.value ?? false;

  // AI settings only make sense with the module on (same gate as the chat widget)
  const { enabled: isAiEnabled } = useIsModuleEnabled(MODULE_KEYS.AsistenteIA);


  const configOptions: ConfigOption[] = [
    {
      key: 'restaurant-settings',
      icon: <ShopOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Restaurante',
      description: 'Información general del negocio, nombre, logo e imagen',
      route: '/restaurant-settings',
      color: '#1890ff',
    },
    {
      key: 'areas-tables',
      icon: <AppstoreOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Áreas y Mesas',
      description: 'Configurar zonas del restaurante y distribución de mesas',
      route: '/areas-tables',
      color: '#52c41a',
    },
    {
      key: 'menu-management',
      icon: <MenuOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Menú',
      description: 'Administrar productos, categorías, precios y disponibilidad',
      route: '/menu-management',
      color: '#fa8c16',
    },
    {
      key: 'user-management',
      icon: <TeamOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Usuarios',
      description: 'Gestión de empleados, roles y permisos de acceso',
      route: '/user-management',
      color: '#722ed1',
    },
    {
      key: 'sucursales',
      icon: <ShopOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Sucursales',
      description: 'Agregar o cancelar sucursales; cada una paga el precio de tu plan',
      route: '/sucursales',
      color: '#0f766e',
    },
    {
      key: 'payment-methods',
      icon: <CreditCardOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Formas de Pago',
      description: 'Configurar métodos de pago aceptados en el restaurante',
      route: '/payment-methods',
      color: '#13c2c2',
    },
    ...(isFacturacionEnabled ? [{
      key: 'facturacion-settings',
      icon: <FileTextOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Facturación',
      description: 'Configuración fiscal, certificados y datos de CFDI',
      route: '/facturacion-settings',
      color: '#eb2f96',
    }] : []),
    {
      key: 'terminal-settings',
      icon: <CreditCardOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Terminales de cobro',
      description: 'Cobrar cuentas enviando el pago a tu terminal bancaria (Mercado Pago o Getnet)',
      route: '/terminal-settings',
      color: '#009ee3',
    },
    {
      key: 'printer-settings',
      icon: <PrinterOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Impresoras',
      description: 'Configurar impresoras, centros de producción e impresión en la nube',
      route: '/printer-settings',
      color: '#3bbbb0',
    },
    ...(isAiEnabled ? [{
      key: 'ai-settings',
      icon: <RobotOutlined style={{ fontSize: isMobile ? 36 : 48 }} />,
      label: 'Asistente IA',
      description: 'Resumen diario por notificación y alertas del asistente',
      route: '/ai-settings',
      color: '#0f9d8f',
    }] : []),
  ];
  // El Menú Digital ya no tiene tarjeta aquí: vive como pestaña dentro de
  // Gestión del Menú, que es donde están los productos que edita. Dos puertas
  // a lo mismo solo obligaban a saltar entre pantallas para publicar la carta.

  // El "próximamente" de proveedores en piloto (hoy Mercado Pago) vive dentro
  // del hub de Terminales de cobro, no aquí.
  const handleOptionClick = (option: ConfigOption) => {
    navigate(option.route);
  };

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <div style={{ marginBottom: isMobile ? 16 : 32, textAlign: 'center' }}>
        <Title level={isMobile ? 3 : 2}>
          <SettingOutlined style={{ marginRight: 12 }} />
          Configuración
        </Title>
        <Text type="secondary">
          Selecciona una opción para administrar tu restaurante
        </Text>
      </div>

      <Row gutter={[isMobile ? 12 : 24, isMobile ? 12 : 24]} justify="center">
        {configOptions.map((option) => (
          <Col xs={12} sm={12} md={8} lg={6} xl={6} key={option.key}>
            <Card
              hoverable
              onClick={() => handleOptionClick(option)}
              style={{
                textAlign: 'center',
                height: '100%',
                borderRadius: 16,
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              styles={{
                body: {
                  padding: isMobile ? 20 : 32,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: isMobile ? 140 : 200,
                },
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px var(--shadow-color-strong)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <div
                style={{
                  color: option.color,
                  marginBottom: isMobile ? 12 : 20,
                  padding: isMobile ? 12 : 16,
                  borderRadius: '50%',
                  backgroundColor: `${option.color}15`,
                }}
              >
                {option.icon}
              </div>
              <Text strong style={{ fontSize: isMobile ? 14 : 16, marginBottom: 8 }}>
                {option.label}
              </Text>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                  {option.description}
                </Text>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ConfiguracionPage;
